
import { useState, useEffect } from "react";
import { NotificationFormData, SelectedOrderProductData } from "@/types/notifications";
import { OrderProduct } from "@/hooks/useOrderProducts";

export const useNotificationForm = (userId: string | undefined, userName: string | undefined) => {
  const [notificationForm, setNotificationForm] = useState<NotificationFormData>({
    type: "",
    voNumber: "",
    exactOrderNumber: "",
    message: "",
    priority: "normaal",
    userId: userId || "",
    userName: userName || "",
    status: "",
    recipients: {
      internal: {
        group1: false,
        group2: false
      },
      external: [] as string[]
    },
    selectedOrderProducts: [] as SelectedOrderProductData[],
    isOverarchingNotification: false,
  });

  useEffect(() => {
    setNotificationForm(prev => ({
      ...prev,
      userId: userId || "",
      userName: userName || "",
    }));
  }, [userId, userName]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setNotificationForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setNotificationForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (name: string, value: string | boolean) => {
    if (name === 'priority') {
      const priorityValue = typeof value === 'boolean'
        ? (value ? 'hoog' : 'normaal')
        : value;
      
      setNotificationForm(prev => ({
        ...prev,
        priority: priorityValue
      }));
    } else {
      setNotificationForm(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleInternalCheckboxChange = (group: 'group1' | 'group2') => {
    setNotificationForm(prev => ({
      ...prev,
      recipients: {
        ...prev.recipients,
        internal: {
          ...prev.recipients.internal,
          [group]: !prev.recipients.internal[group]
        }
      }
    }));
  };

  const toggleExternalRecipient = (recipient: string) => {
    setNotificationForm(prev => {
      const isSelected = prev.recipients.external.includes(recipient);
      return {
        ...prev,
        recipients: {
          ...prev.recipients,
          external: isSelected
            ? prev.recipients.external.filter(r => r !== recipient)
            : [...prev.recipients.external, recipient]
        }
      };
    });
  };


  // Order product selection handlers
  const handleToggleOrderProduct = (product: OrderProduct) => {
    setNotificationForm(prev => {
      const existing = prev.selectedOrderProducts || [];
      const isSelected = existing.some(p => p.sku === product.sku);
      const updated = isSelected
        ? existing.filter(p => p.sku !== product.sku)
        : [...existing, { ...product, productMessage: "" }];
      return { ...prev, selectedOrderProducts: updated };
    });
  };

  const handleOrderProductMessageChange = (sku: string, message: string) => {
    setNotificationForm(prev => {
      const updated = (prev.selectedOrderProducts || []).map(p =>
        p.sku === sku ? { ...p, productMessage: message } : p
      );
      return { ...prev, selectedOrderProducts: updated };
    });
  };

  const handleOverarchingChange = (value: boolean) => {
    setNotificationForm(prev => ({
      ...prev,
      isOverarchingNotification: value
    }));
  };

  return {
    notificationForm,
    handleInputChange,
    handleSelectChange,
    handleCheckboxChange,
    handleInternalCheckboxChange,
    toggleExternalRecipient,
    handleToggleOrderProduct,
    handleOrderProductMessageChange,
    handleOverarchingChange,
  };
};
